import { useRef, useEffect, useState, useMemo } from "react";
import Link from "next/link";
import analytics from "../util/analytics";
import { Steps } from "./NewSlideSteps";
import { marked } from "marked";
import {
  ArrowDownOnSquareIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";

import "reveal.js/dist/reveal.css";
import "reveal.js/dist/theme/league.css";
import { updateSlide } from "../util/db";

function ShowSlide({ slideId, isOwner, slide, searchParams: search }) {
  const revealRef = useRef(null);
  const [deck, setDeck] = useState();

  const memo = useMemo(() => new URLSearchParams(search), [search]);
  // TODO: It renders unnecessary elements including hidden layout elements.
  // Create a new page for print mode only.
  const isPrintMode = memo.get("print-pdf");

  const [isPublic, setIsPublic] = useState(slide?.isPublic);
  let publicURL = undefined;
  if (typeof window !== "undefined") {
    publicURL = window && window.location.href;
  }
  const publicURLRef = useRef();
  const [publicURLCopied, setPublicURLCopied] = useState(false);

  useEffect(() => {
    setIsPublic(slide?.isPublic);
  }, [slide]);

  useEffect(() => {
    if (isOwner) {
      updateSlide(slide.id, { isPublic });
    }
  }, [isOwner, slide, isPublic]);

  const tabs = [
    {
      name: "prompt",
      label: "Topic",
      content: (s) => s?.prompt,
      step: Steps.StepPrompt,
    },
    {
      name: "toc",
      label: "Outline",
      content: (s) => marked.parse(s?.toc || ""),
      step: Steps.StepTOC,
    },
    {
      name: "script",
      label: "Notes",
      content: (s) => marked.parse(s?.script || ""),
      step: Steps.StepScript,
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[2]);
  const [content, setContent] = useState();

  useEffect(() => {
    const clientSideInitialization = async () => {
      const Reveal = (await import("reveal.js")).default;
      const Markdown = (await import("reveal.js/plugin/markdown/markdown.esm"))
        .default;
      if (slide?.markdown) {
        const deck = new Reveal(revealRef.current, {
          plugins: [Markdown],
          embedded: true,
        });
        setDeck(deck);
      }
    };
    clientSideInitialization();
  }, [slide.markdown]);

  useEffect(() => {
    if (deck) {
      deck.initialize({ hash: true, disableLayout: false });

      // TODO: Find the proper callback to check if the deck is rendered properly.
      if (isPrintMode) {
        setTimeout(() => {
          window.print();
        }, 1000);
      }
    }
  }, [deck]);

  useEffect(() => {
    setContent(activeTab.content(slide));
  }, [slide, activeTab]);

  const onClickFullscreenButton = async () => {
    const { enterFullscreen } = await import("reveal.js/js/utils/util.js");
    analytics.track("click_full_screen");
    deck && enterFullscreen(deck.getViewportElement());
  };

  const copyPublicURLToClipboard = async () => {
    if (!navigator) return;
    if ("clipboard" in navigator) {
      await navigator.clipboard.writeText(publicURL);
    } else {
      publicURLRef.current.select();
      publicURLRef.current.setSelectionRange(0, publicURL.length);
      document.execCommand("copy");
    }
    setPublicURLCopied(true);
    setTimeout(() => setPublicURLCopied(false), 2000);
  };

  return (
    <section className="md:py-12 p-4">
      <div className="container mx-auto max-w-2xl">
        <div className="md:flex mb-3 md:justify-between">
          <h2 className="text-3xl font-bold">
            {slide?.title || <span className="text-gray-600">Untitled</span>}
          </h2>

          <div className="flex items-end justify-end mt-2 md:mt-0 leading-10">
            <button
              className="hidden md:inline-flex btn btn-sm md:btn-xs btn-square"
              title="Full screen"
              onClick={onClickFullscreenButton}
            >
              <ArrowsPointingOutIcon className="w-5 h-5 md:w-4 md:h-4" />
            </button>
            <Link
              href={`/slides/${slideId}?print-pdf=true`}
              target="_blank"
              title="Export"
              className="btn btn-sm md:btn-xs btn-square ml-1"
            >
              <ArrowDownOnSquareIcon className="w-5 h-5 md:w-4 md:h-5" />
            </Link>
            {isOwner && (
              <>
                <div className="dropdown dropdown-bottom dropdown-end flex">
                  <label
                    title="Share"
                    tabIndex={0}
                    className="btn btn-sm md:btn-xs btn-square ml-1"
                  >
                    <ArrowTopRightOnSquareIcon className="w-5 h-5 md:w-4 md:h-4" />
                  </label>
                  <div
                    tabIndex={0}
                    className="dropdown-content mt-2 p-2 shadow-xl bg-base-200 rounded-box w-72"
                  >
                    <div className="form-control w-full">
                      <label className="cursor-pointer label">
                        <span className="label-text font-semibold">
                          Share to Web
                        </span>
                        <input
                          type="checkbox"
                          className="toggle toggle-success"
                          onChange={(e) => {
                            setIsPublic(e.target.checked);
                          }}
                          checked={isPublic}
                        />
                      </label>
                    </div>
                    {isPublic && (
                      <div className="flex">
                        <input
                          ref={publicURLRef}
                          type="text"
                          className="input w-full max-w-xs"
                          value={publicURL}
                          readOnly={true}
                        ></input>
                        <button
                          className="btn ml-2"
                          onClick={copyPublicURLToClipboard}
                        >
                          {publicURLCopied ? (
                            <CheckIcon className="h-6" />
                          ) : (
                            <DocumentDuplicateIcon className="h-6" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <Link
                  href={`/slides/${slideId}/edit?step=${Steps.StepSlide}`}
                  shallow={true}
                  className="btn btn-sm md:btn-xs ml-1 btn-ghost"
                >
                  Edit
                </Link>
              </>
            )}
          </div>
        </div>

        <div
          className=""
          style={
            isPrintMode && {
              width: "100%",
              height: "100%",
              position: "absolute",
              left: 0,
              top: 0,
            }
          }
        >
          <div className="reveal" ref={revealRef}>
            <div className="slides">
              <section data-markdown="">
                <textarea
                  data-template=""
                  value={slide?.markdown}
                  readOnly={true}
                ></textarea>
              </section>
            </div>
          </div>
        </div>

        <div className="tabs mt-5">
          {tabs.map((tab) => (
            <a
              key={tab.name}
              className={
                tab.name === activeTab.name
                  ? "tab tab-lg tab-bordered tab-active"
                  : "tab tab-lg tab-bordered"
              }
              onClick={() => setActiveTab(tab)}
            >
              {tab.label}
            </a>
          ))}
        </div>

        <div
          className="mt-4 markdown-parsed"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {isOwner && (
            <Link
              href={`/slides/${slideId}/edit?step=${activeTab.step}`}
              shallow={true}
              className="btn btn-xs float-right mr-2"
            >
              Edit
            </Link>
          )}
          <p dangerouslySetInnerHTML={{ __html: content }}></p>
        </div>
      </div>
    </section>
  );
}

export default ShowSlide;
